import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/authStore'

const API_URL = import.meta.env.VITE_API_URL || '/api/v1'

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = useAuthStore.getState().refreshToken
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          })

          const { access_token, refresh_token } = response.data
          useAuthStore.getState().setTokens(access_token, refresh_token)

          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch (refreshError) {
          useAuthStore.getState().logout()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      }

      useAuthStore.getState().logout()
      window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

// API helper functions
export const authAPI = {
  requestOTP: (identifier: string, type: 'email' | 'phone') =>
    api.post('/auth/request-otp', { identifier, type }),

  verifyOTP: (identifier: string, otp: string, type: 'email' | 'phone') =>
    api.post('/auth/verify-otp', { identifier, otp, type }),

  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refresh_token: refreshToken }),

  getMe: () => api.get('/auth/me'),

  logout: () => api.post('/auth/logout'),
}

export const doctorsAPI = {
  list: (params?: Record<string, unknown>) => api.get('/doctors', { params }),
  get: (id: string) => api.get(`/doctors/${id}`),
  create: (data: Record<string, unknown>) => api.post('/doctors', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/doctors/${id}`, data),
  delete: (id: string) => api.delete(`/doctors/${id}`),
  copy: (id: string, code: string) => api.post(`/doctors/${id}/copy`, { code }),
  createAddress: (doctorId: string, data: Record<string, unknown>) =>
    api.post(`/doctors/${doctorId}/addresses`, data),
  deleteAddress: (doctorId: string, addressId: string) =>
    api.delete(`/doctors/${doctorId}/addresses/${addressId}`),
}

export const workItemsAPI = {
  list: (params?: Record<string, unknown>) => api.get('/work-items', { params }),
  get: (id: string) => api.get(`/work-items/${id}`),
  create: (data: Record<string, unknown>) => api.post('/work-items', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/work-items/${id}`, data),
  delete: (id: string) => api.delete(`/work-items/${id}`),
  categories: () => api.get('/work-items/categories'),
}

export const workOrdersAPI = {
  list: (params?: Record<string, unknown>) => api.get('/work-orders', { params }),
  get: (id: string) => api.get(`/work-orders/${id}`),
  create: (data: Record<string, unknown>) => api.post('/work-orders', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/work-orders/${id}`, data),
  updateStatus: (id: string, status: string) => api.put(`/work-orders/${id}/status`, { status }),
  dispatch: (id: string, data: Record<string, unknown>) => api.put(`/work-orders/${id}/dispatch`, data),
  delete: (id: string) => api.delete(`/work-orders/${id}`),
}

export const invoicesAPI = {
  list: (params?: Record<string, unknown>) => api.get('/invoices', { params }),
  get: (id: string) => api.get(`/invoices/${id}`),
  create: (data: Record<string, unknown>) => api.post('/invoices', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/invoices/${id}`, data),
  delete: (id: string) => api.delete(`/invoices/${id}`),
}

export const paymentsAPI = {
  list: (params?: Record<string, unknown>) => api.get('/payments', { params }),
  get: (id: string) => api.get(`/payments/${id}`),
  create: (data: Record<string, unknown>) => api.post('/payments', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/payments/${id}`, data),
  delete: (id: string) => api.delete(`/payments/${id}`),
}

export const dashboardAPI = {
  get: (period?: string) => api.get('/dashboard', { params: { period } }),
}

export const duesAPI = {
  summary: () => api.get('/dues'),
  doctor: (id: string) => api.get(`/dues/doctors/${id}`),
  statement: (id: string, params?: Record<string, unknown>) =>
    api.get(`/statements/doctors/${id}`, { params }),
}

export const settingsAPI = {
  list: () => api.get('/settings'),
  get: (key: string) => api.get(`/settings/${key}`),
  update: (key: string, value: string, description?: string) =>
    api.put(`/settings/${key}`, { value, description }),
  getLabProfile: () => api.get('/settings/lab-profile'),
  updateLabProfile: (data: Record<string, unknown>) => api.put('/settings/lab-profile', data),
}

export const usersAPI = {
  list: () => api.get('/users'),
  create: (data: Record<string, unknown>) => api.post('/users', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
}

export const auditAPI = {
  list: (params?: Record<string, unknown>) => api.get('/audit-logs', { params }),
}

export const reportsAPI = {
  sales: (params?: Record<string, unknown>) => api.get('/reports/sales', { params }),
  payments: (params?: Record<string, unknown>) => api.get('/reports/payments', { params }),
  gst: (params?: Record<string, unknown>) => api.get('/reports/gst', { params }),
  monthlySales: (params: { month: string; year: string; doctor_id?: string }) =>
    api.get('/reports/monthly-sales', { params }),
  monthlySalesCollection: (params: { month: string; year: string; doctor_id?: string }) =>
    api.get('/reports/monthly-sales-collection', { params }),
}

export const supportAPI = {
  createTicket: (data: Record<string, unknown>) => api.post('/support/tickets', data),
  getTicket: (ticketId: string) => api.get(`/support/tickets/${ticketId}`),
  chat: (message: string, topic?: string) => api.post('/support/chat', { message, topic }),
}

export default api
